import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/shallow';

import { useGetProjects } from 'api/queries/projects/projects';
import { createProjectSignature } from 'app/modules/wallforming/components/projects-manager/utils/projectUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { EMPTY_GUID } from 'shared/constants';

/**
 * Fetches the project list from the API and provides all derived project-related
 * data consumed by the project manager:
 * - `resolvedActiveProject` — merges the URL `projectId` param with the store's
 *   `activeProject`, keeping the name/description in sync with the server.
 * - `currentProjectSignature` — JSON snapshot of articles + POI assignments used
 *   for dirty-state comparison.
 * - `activeSessionId` — resolves the SignalR session from the store or falls back
 *   to the `formingsession` URL param.
 */
export const useProjectsData = () => {
    const [searchParams] = useSearchParams();

    const { isOpen, articles, pois, sessionId, lastSavedSignature, activeProject } = usePlacementStore(
        useShallow((state) => ({
            isOpen: state.isProjectPanelOpen,
            articles: state.articles,
            pois: state.pois,
            sessionId: state.sessionId,
            lastSavedSignature: state.lastSavedSignature,
            activeProject: state.activeProject
        }))
    );

    const urlProjectId = useMemo(() => searchParams.get('projectId') ?? EMPTY_GUID, [searchParams]);
    const urlSessionId = useMemo(() => searchParams.get('formingsession') ?? EMPTY_GUID, [searchParams]);
    const activeSessionId = useMemo(
        () => (sessionId !== EMPTY_GUID ? sessionId : urlSessionId),
        [sessionId, urlSessionId]
    );

    const { data: projectsResponse, isPending: isProjectsPending } = useGetProjects({
        query: { staleTime: 30_000, enabled: isOpen || Boolean(urlProjectId) }
    });

    const projects = useMemo(() => projectsResponse?.projects ?? [], [projectsResponse?.projects]);
    const projectFromUrl = useMemo(
        () => projects.find((project) => project.id === urlProjectId) ?? null,
        [projects, urlProjectId]
    );

    const resolvedActiveProject = useMemo(() => {
        if (!activeProject) {
            return projectFromUrl;
        }

        const matchingProject = projects.find((project) => project.id === activeProject.id);
        if (!matchingProject) {
            return activeProject;
        }

        return {
            id: matchingProject.id,
            name: matchingProject.name,
            description: matchingProject.description ?? null
        };
    }, [activeProject, projectFromUrl, projects]);

    const currentProjectSignature = useMemo(() => createProjectSignature(articles, pois), [articles, pois]);

    return {
        projects,
        isProjectsPending,
        resolvedActiveProject,
        currentProjectSignature,
        lastSavedSignature,
        activeSessionId,
        urlProjectId
    };
};
